import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Tooltip from "@mui/material/Tooltip";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Alert from "@mui/material/Alert";
import Collapse from "@mui/material/Collapse";

import MenuIcon from "@mui/icons-material/Menu";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import { DRAWER_WIDTH, APPBAR_HEIGHT } from "theme/muiTheme";
import CambioClaveForm from "components/CambioClaveForm";

export default function AdminNavbar({ onMenuClick, brandText }) {
  const history = useHistory();
  const [usuario, setUsuario] = useState("");
  const [nombre, setNombre] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [cambioOpen, setCambioOpen] = useState(false);
  const [successMsg, setSuccessMsg] = useState(false);

  useEffect(() => {
    setUsuario(localStorage.getItem("usuario") || "");
    setNombre(localStorage.getItem("nombre") || "");
  }, []);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleLogout = () => {
    handleMenuClose();
    localStorage.removeItem("token");
    localStorage.removeItem("usuario");
    localStorage.removeItem("nombre");
    history.push("/auth/login");
  };

  const handleCambioOpen = () => {
    handleMenuClose();
    setCambioOpen(true);
  };

  const handleCambioClose = () => setCambioOpen(false);

  const handleCambioSuccess = () => {
    setCambioOpen(false);
    setSuccessMsg(true);
    setTimeout(() => setSuccessMsg(false), 3000);
  };

  const initials =
    nombre && nombre.length > 0
      ? nombre.trim().charAt(0).toUpperCase()
      : usuario
        ? usuario.trim().charAt(0).toUpperCase()
        : "U";

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { xs: "100%", md: `calc(100% - ${DRAWER_WIDTH}px - 40px)` },
          ml: { xs: 0, md: `${DRAWER_WIDTH}px` },
          background:
            "linear-gradient(112.83deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.88) 110.84%)",
          backdropFilter: "blur(21px)",
          borderBottom: "1.5px solid rgba(255,255,255,0.8)",
          color: "text.primary",
          minHeight: `${APPBAR_HEIGHT}px`,
          top: { xs: 0, md: "18px" },
          right: { xs: 0, md: "20px" },
          borderRadius: { xs: 0, md: "16px" },
        }}
      >
        <Toolbar
          sx={{ minHeight: `${APPBAR_HEIGHT}px !important`, px: { xs: 2, md: 3 }, gap: 1 }}
        >
          <IconButton
            edge="start"
            onClick={onMenuClick}
            sx={{ display: { xs: "flex", md: "none" }, mr: 1, color: "text.primary" }}
            aria-label="abrir menú"
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 0.25 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                Páginas
              </Typography>
              <Typography variant="caption" color="text.primary" sx={{ fontWeight: 700 }}>
                {brandText}
              </Typography>
            </Breadcrumbs>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              color="text.primary"
              noWrap
              sx={{ lineHeight: 1.2 }}
            >
              {brandText}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
            <Collapse in={successMsg} orientation="horizontal">
              <Alert severity="success" sx={{ py: 0, borderRadius: 2, whiteSpace: "nowrap" }}>
                Contraseña actualizada
              </Alert>
            </Collapse>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ display: { xs: "none", sm: "block" }, fontWeight: 500, whiteSpace: "nowrap" }}
            >
              {usuario}{nombre ? ` — ${nombre}` : ""}
            </Typography>

            <Tooltip title="Mi cuenta">
              <IconButton onClick={handleMenuOpen} size="small" sx={{ p: 0 }}>
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: "primary.main",
                    fontSize: "0.875rem",
                    fontWeight: "bold",
                  }}
                >
                  {initials}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
            PaperProps={{
              elevation: 3,
              sx: {
                mt: 1,
                minWidth: 200,
                borderRadius: 2,
                overflow: "visible",
                "&:before": {
                  content: '""',
                  display: "block",
                  position: "absolute",
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: "background.paper",
                  transform: "translateY(-50%) rotate(45deg)",
                  zIndex: 0,
                },
              },
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <MenuItem disabled>
              <ListItemIcon>
                <PersonOutlineIcon fontSize="small" />
              </ListItemIcon>
              <Typography variant="body2" noWrap>
                {usuario}{nombre ? ` — ${nombre}` : ""}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleCambioOpen}>
              <ListItemIcon>
                <LockOutlinedIcon fontSize="small" />
              </ListItemIcon>
              <Typography variant="body2">Cambiar contraseña</Typography>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" color="error" />
              </ListItemIcon>
              <Typography variant="body2" color="error">
                Cerrar sesión
              </Typography>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Modal cambio de contraseña */}
      <Dialog
        open={cambioOpen}
        onClose={handleCambioClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle
          sx={{
            fontFamily: "'Nohemi', Arial, sans-serif",
            fontWeight: 600,
            fontSize: "1.3rem",
            pb: 0,
          }}
        >
          Cambiar contraseña
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Actualizá tu contraseña de acceso
          </Typography>
          <CambioClaveForm
            onSuccess={handleCambioSuccess}
            onCancel={handleCambioClose}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
